import {Injectable, inject} from '@angular/core'
import {PrimeNGConfig} from 'primeng/api'
import {DateUtil} from '@common/utils/date.util'
import {TranslationServicex} from './translationx.service'

/**
 * Configuración i18n de PrimeNG en función del locale de la app.
 *
 * @description
 * Ajusta `PrimeNGConfig` (nombres de días/meses, header de semana, primer
 * día de la semana = lunes, traducción de "today") tomando el locale activo
 * desde `TranslationServicex` y mapeándolo a un locale válido para la API
 * `Intl`. Soporta `es`, `en`/`en-GB`, `fr`/`fr-FR` y `ca`; cualquier otro
 * valor cae en `es-ES` como fallback.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * Se llama típicamente desde `AppInitializer` al arrancar la app:
 *
 * ```typescript
 * private readonly primeSetup = inject(PrimeNGSetupService)
 *
 * await this.primeSetup.initializePrimeNG()
 * ```
 *
 * ### Best Practices
 *
 * - Re-invocar tras un cambio de idioma para que los componentes PrimeNG
 *   (calendar, table, etc.) reflejen el nuevo locale.
 */
@Injectable({
  providedIn: 'root',
})
export class PrimeNGSetupService {
  private readonly primeConfig = inject(PrimeNGConfig)
  private readonly translationServicex = inject(TranslationServicex)

  /**
   * Aplica la traducción/locale a `PrimeNGConfig` según el idioma actual.
   *
   * @description
   * No hace nada si el `TranslationServicex.getLocale` no devuelve un string.
   */
  async initializePrimeNG(): Promise<void> {
    const locale = await this.translationServicex.getLocale?.()
    if (typeof locale !== 'string') {
      return
    }

    const localeMap = this.getLocaleMapping(locale)
    const config = this.generatePrimeNGConfig(localeMap)
    this.primeConfig.setTranslation(config)
  }

  /**
   * Mapea el locale de la aplicación a un locale válido para Intl
   */
  private getLocaleMapping(locale: string): string {
    const localeMap: Record<string, string> = {
      es: 'es-ES',
      en: 'en-GB',
      'en-GB': 'en-GB',
      fr: 'fr-FR',
      'fr-FR': 'fr-FR',
      ca: 'ca-ES',
    }

    return localeMap[locale] || 'es-ES'
  }

  /**
   * Genera la configuración de PrimeNG dinámicamente usando Intl y traducciones
   */
  private readonly generatePrimeNGConfig = (locale: string) => ({
    firstDayOfWeek: 1, // Lunes/Monday
    today: this.translationServicex.instant('today'),
    dayNames: DateUtil.getDayNames(locale),
    dayNamesShort: DateUtil.getDayNames(locale, 'short'),
    dayNamesMin: DateUtil.getDayNames(locale, 'narrow'),
    monthNames: DateUtil.getMonthNames(locale),
    monthNamesShort: DateUtil.getMonthNames(locale, 'short'),
    weekHeader: this.getWeekHeader(locale),
  })

  /**
   * Obtiene el header de semana según el locale
   */
  private readonly getWeekHeader = (locale: string): string => {
    const weekHeaders: Record<string, string> = {
      'es-ES': 'Sem',
      'en-GB': 'Wk',
      'fr-FR': 'Sem',
      'ca-ES': 'Set',
    }

    return weekHeaders[locale] || 'Sem'
  }
}
